// React state and router.
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom';

// Redux.
import { connect } from 'react-redux';
import {
    restoreNextPreviewsInfo,
    setNextPreviewsInfo,
} from '../redux/actions/dataActions';

import {
    loadForMePreviews,
    retryForMeRefreshIfNeeded,
} from '../redux/actions/userActions';

// Firebase.
import { getAnalytics, logEvent } from "firebase/analytics";


// Components.
import AudioPlayerDrawer from '../components/AudioPlayerDrawer';
import PreviewCard from '../components/PreviewCard';
import Loading from '../components/Loading';
import OnboardingHighlightsReady from '../components/OnboardingHighlights';


// Component helpers.
import { loadTrack } from "../components/audioHelper"

// Constants.
import { useLocation, useNavigate } from 'react-router-dom';
import useWindowDimensions from '../components/windowHelper';
import { ChevronLeftRounded, ChevronRightRounded } from '@mui/icons-material';
import { eventLog } from '../components/eventHelper';
import { TIP_PREVIEW_PAGER_SWIPE_LEARNED_V1 } from '../constants/local_storage_constants';

import { PREVIEWS_DESKTOP_NAV_MIN_WIDTH } from '../constants/sizes';
import {
    clearOnboardingSession,
    readOnboardingSession,
    requestOnboardingSignup,
    updateOnboardingSession,
    selectOnboardingHighlight,
    recordOnboardingPlayback,
    finishOnboardingHighlight,
} from '../utils/onboardingSession';
import { persistGetStartedDraft } from '../utils/getStartedSelections';
import { GET_STARTED_NEW_USER_RESPONSE } from '../constants/local_storage_constants';
import { trackOnboardingError, trackOnboardingEvent } from '../utils/onboardingFunnel';
import { getPreviewViewerSession, savePreviewViewerSession } from '../utils/previewViewerSessions';
import { isPlayableChatPreview } from '../utils/chatPreviewQueue';
import { finishActivePreviewListening } from '../utils/previewListening';
import { getAppViewport, isAppFramed } from '../utils/appViewport';

// The arrow gutters sit outside the scrolling canvas. Keep both painting and
// hit testing outside its clipping ancestors while retaining desktop controls.
const renderDesktopNavigation = navigation => {
    const viewport = document.getElementById('appViewport');
    return viewport ? createPortal(navigation, viewport) : navigation;
};

const _NUM_PREVIEWS_TO_PREFETCH = 1;
const _PREVIEW_PAGER_PEEK_DELAY_MS = 5000;
const _PREVIEW_PAGER_PEEK_OUT_MS = 240;
const _PREVIEW_PAGER_PEEK_HOLD_MS = 140;
const _PREVIEW_PAGER_PEEK_BACK_MS = 320;
const _PREVIEW_PAGER_REDUCED_MOTION_CUE_MS = 1800;
const _PREVIEW_PAGER_SETTLE_DELAY_MS = 220;
const _PREVIEW_PAGER_POINTER_SWIPE_MIN_PX = 12;

const readPreviewPagerSwipeLearned = () => {
    try {
        return window.localStorage.getItem(TIP_PREVIEW_PAGER_SWIPE_LEARNED_V1) === 'true';
    } catch (_) {
        return false;
    }
}

const writePreviewPagerSwipeLearned = () => {
    try {
        window.localStorage.setItem(TIP_PREVIEW_PAGER_SWIPE_LEARNED_V1, 'true');
    } catch (_) {
        // Keep the learned state in memory when browser storage is unavailable.
    }
}

export const previewPagerPageIndex = (scrollLeft, pageWidth, lastPageIndex) => {
    if (!Number.isFinite(scrollLeft) || !Number.isFinite(pageWidth) || pageWidth <= 0 || lastPageIndex < 0) {
        return -1;
    }
    return Math.max(0, Math.min(lastPageIndex, Math.round(scrollLeft / pageWidth)));
}

export const shouldLearnPreviewPagerSwipe = ({
    qualified,
    didScroll,
    startIndex,
    settledIndex,
    realPreviewCount,
}) =>
    qualified &&
    didScroll &&
    startIndex >= 0 &&
    startIndex < realPreviewCount &&
    settledIndex >= 0 &&
    settledIndex < realPreviewCount &&
    settledIndex !== startIndex;

const PreviewSession = ({
    // Redux state.
    authenticated,
    forMePreviewsData,
    forMeLoadingCount,
    forMeLoadError,
    nextPreviewsInfo,
    // TODO create new redux field preview that differs from track.
    // Currently both preview and track use "track" field.
    // A preview has additional fields such as "fullTrackId" and "relatedTopicPreviews"
    track,

    // Redux actions.
    loadForMePreviews,
    retryForMeRefreshIfNeeded,
    setNextPreviewsInfo,
    onboardingRecovery = false,
    leavingOnboarding,
    resumePreviewPosition,
    onSessionSnapshot,
}) => {
    const analytics = getAnalytics();
    const audio = document.getElementById('audio');
    const navigate = useNavigate();
    const { width } = useWindowDimensions();
    const location = useLocation();
    const { state } = location;
    const [onboardingSession, setOnboardingSession] = useState(() => {
        const session = readOnboardingSession();
        const queue = nextPreviewsInfo?.previewsInfo;
        const explicitEntry = state?.onboarding || state?.onboardingExit || onboardingRecovery;
        return session && explicitEntry && (session.signedIn || !queue?.length || (queue.length === 1 && queue[0].id === session.sample.id))
            ? session : null;
    });
    const onboardingGuest = Boolean(onboardingSession && !authenticated);
    const showOnboardingReady = onboardingGuest && onboardingSession.phase === 'ready';
    const showOnboardingComplete = onboardingGuest && onboardingSession.phase === 'complete';
    const coverOnboardingPlayer = showOnboardingReady || showOnboardingComplete;
    const onboardingChoices = onboardingSession?.starterHighlights.filter(preview =>
        preview.id === onboardingSession.sample.id || !onboardingSession.completedPreviewIds.includes(preview.id)) || [];
    const [onboardingPlayPending, setOnboardingPlayPending] = useState(false);
    const [onboardingPlayError, setOnboardingPlayError] = useState(null);
    const onboardingPlayAttempt = useRef(0);
    const onboardingPlaybackFocus = useRef(false);
    const onboardingPreparedPlayerRef = useRef(null);
    const [onboardingSampleStarted, setOnboardingSampleStarted] = useState(() => Boolean(onboardingSession?.sampleStarted));
    const initialPreviewIndex = Math.max(0, nextPreviewsInfo?.previewsInfo.findIndex(
        preview => preview.id === nextPreviewsInfo.initialPreviewId,
    ) ?? 0);

    const [currentPreviewIndex, setCurrentPreviewIndex] = useState(initialPreviewIndex);
    const playbackHintPreviewId = useRef(nextPreviewsInfo?.previewsInfo[initialPreviewIndex]?.id);
    const [playbackHintExpired, setPlaybackHintExpired] = useState(false);
    const expirePlaybackHint = useCallback(() => setPlaybackHintExpired(true), []);
    useEffect(() => {
        const selectedId = nextPreviewsInfo?.previewsInfo[currentPreviewIndex]?.id;
        if (!playbackHintPreviewId.current) playbackHintPreviewId.current = selectedId;
        else if (selectedId && selectedId !== playbackHintPreviewId.current) expirePlaybackHint();
    }, [currentPreviewIndex, nextPreviewsInfo, expirePlaybackHint]);
    const [queuedPreviewSelection, setQueuedPreviewSelection] = useState(null);
    const [queuePreviewId, setQueuePreviewId] = useState(null);
    const [playbackEnabled, setPlaybackEnabled] = useState(false);
    const initialPositionApplied = useRef(false);
    const [previewPlayingAudioURL, setPreviewPlayingAudioURL] = useState(
        () => audio && !audio.paused && !audio.ended && audio.readyState >= 2 ? audio.src : null,
    );
    const [pageIsVisible, setPageIsVisible] = useState(() => !document.hidden);
    const [showReducedMotionSwipeCue, setShowReducedMotionSwipeCue] = useState(false);
    const [closeButtonHost, setCloseButtonHost] = useState(null);
    const [controlsHost, setControlsHost] = useState(null);
    const previewsScrollContainerRef = useRef(null);
    const previewsTrackRef = useRef(null);
    const previewPagerPeekTimerRef = useRef(null);
    const previewPagerReducedMotionCueTimerRef = useRef(null);
    const previewPagerPeekAnimationRef = useRef(null);
    const previewPagerPeekScheduledRef = useRef(false);
    const previewPagerPeekShownForAppearanceRef = useRef(false);
    const previewPagerPeekCancelledForAppearanceRef = useRef(false);
    const previewPagerSwipeLearnedRef = useRef(readPreviewPagerSwipeLearned());
    const previewPagerGestureRef = useRef(null);
    const previewPagerTouchGestureRef = useRef(null);
    const previewPagerSettleTimerRef = useRef(null);

    const isIntentionalPreviewExit = (previewIndex) => {
        const gesture = previewPagerGestureRef.current || previewPagerTouchGestureRef.current;
        const container = previewsScrollContainerRef.current;
        return Boolean(container && gesture?.qualified && gesture.didScroll
            && previewPagerPageIndex(container.scrollLeft, container.clientWidth, previewCount - 1) !== previewIndex);
    };

    useLayoutEffect(() => {
        const preview = nextPreviewsInfo?.previewsInfo?.[currentPreviewIndex];
        if (!preview || !onSessionSnapshot || onboardingSession) return undefined;
        const snapshot = { location, queue: nextPreviewsInfo, previewId: preview.id, position: 0 };
        const capturePosition = () => {
            if (audio && audio.src === preview.mp3URL) snapshot.position = audio.currentTime;
        };
        capturePosition();
        onSessionSnapshot(snapshot);
        audio?.addEventListener('timeupdate', capturePosition);
        audio?.addEventListener('pause', capturePosition);
        return () => {
            audio?.removeEventListener('timeupdate', capturePosition);
            audio?.removeEventListener('pause', capturePosition);
        };
    }, [audio, currentPreviewIndex, location, nextPreviewsInfo, onSessionSnapshot, onboardingSession]);


    const finishedPreviewIds = useRef(new Set());
    const startedOnboardingIds = useRef(new Set(onboardingSession?.heardPreviewIds || []));
    const signupRequested = useRef(false);
    const memberQueueRequested = useRef(false);

    const openOnboardingSignup = useCallback((action, reason = 'action') => {
        if (!onboardingGuest || signupRequested.current) return;
        signupRequested.current = true;
        const position = audio?.src === onboardingSession.sample.mp3URL ? audio.currentTime : onboardingSession.position;
        audio?.pause();
        requestOnboardingSignup({ reason, action, position });
        navigate('/signin_new_user', { replace: true });
    }, [audio, navigate, onboardingGuest, onboardingSession]);

    const selectOnboardingPreview = preview => {
        const session = selectOnboardingHighlight(preview.id);
        if (!session) return;
        audio?.pause();
        if (audio) audio.currentTime = 0;
        onboardingPlayAttempt.current += 1;
        setOnboardingSession(session);
        setOnboardingSampleStarted(false);
        setOnboardingPlayPending(false);
        setOnboardingPlayError(null);
        // Prepare the chosen card before the next play gesture. Replacing its
        // queue during Play would mount a card that pauses the new audio.
        setNextPreviewsInfo(false, [session.sample], { initialPreviewId: session.sample.id });
        logEvent(analytics, 'onboarding_highlight_selected', { sample_id: session.sample.id });
    };

    const playOnboardingHighlight = event => {
        if (!audio || onboardingPlayPending) return;
        onboardingPlaybackFocus.current = event?.currentTarget === document.activeElement;
        const sample = onboardingSession.sample;
        const attempt = ++onboardingPlayAttempt.current;
        setOnboardingPlayError(null);
        setOnboardingPlayPending(true);
        if (audio.src !== sample.mp3URL) audio.src = sample.mp3URL;
        else if (audio.error) audio.load();
        if (audio.ended || onboardingSession.sampleCompleted) {
            audio.currentTime = 0;
            updateOnboardingSession({ position: 0 });
        }
        // Stay inside the button's user gesture, including on mobile Safari.
        // The prepared PreviewCard remains mounted when the ready view hides.
        let playRequest;
        try {
            playRequest = audio.play();
        } catch (error) {
            playRequest = Promise.reject(error);
        }
        Promise.resolve(playRequest).then(() => {
            if (attempt !== onboardingPlayAttempt.current) return;
            const session = updateOnboardingSession({ phase: 'preview' });
            setOnboardingSession(session);
            setOnboardingPlayPending(false);
        }).catch(() => {
            if (attempt !== onboardingPlayAttempt.current) return;
            onboardingPlaybackFocus.current = false;
            setOnboardingPlayPending(false);
            setOnboardingPlayError('We couldn’t start this highlight. Try playing it again, or choose another.');
            trackOnboardingError('playback', { code: audio.error ? 'media_error' : 'play_rejected' });
        });
    };

    const completeOnboardingPreview = useCallback(previewId => {
        const current = readOnboardingSession();
        if (!onboardingGuest || current?.phase !== 'preview' || current.sample.id !== previewId
            || current.newId !== onboardingSession.newId) return;
        if (!current.completedPreviewIds.includes(current.sample.id)) {
            trackOnboardingEvent('onboarding_sample_complete');
        }
        finishOnboardingHighlight(audio?.currentTime || 0);
        openOnboardingSignup(null, 'complete');
    }, [audio, onboardingGuest, onboardingSession?.newId, openOnboardingSignup]);

    const changeOnboardingPicks = () => {
        leavingOnboarding.current = true;
        audio?.pause();
        onboardingPlayAttempt.current += 1;
        persistGetStartedDraft(onboardingSession);
        clearOnboardingSession();
        localStorage.removeItem(GET_STARTED_NEW_USER_RESPONSE);
        setNextPreviewsInfo(false, null);
        navigate('/#explore', { replace: true });
    };

    useEffect(() => {
        if (showOnboardingReady) logEvent(analytics, 'onboarding_ready_view', {
            sample_id: onboardingSession.sample.id,
            starter_choice_count: onboardingSession.starterHighlights.length,
            ...(onboardingSession.rosterVersion ? { roster_version: onboardingSession.rosterVersion } : {}),
        });
    }, [analytics, showOnboardingReady, onboardingSession?.sample.id, onboardingSession?.starterHighlights.length, onboardingSession?.rosterVersion]);

    useEffect(() => () => { onboardingPlayAttempt.current += 1; }, []);

    useEffect(() => {
        if (!onboardingGuest || coverOnboardingPlayer || !onboardingPlaybackFocus.current) return;
        const control = onboardingPreparedPlayerRef.current?.querySelector(
            'button[aria-label="Pause highlight"], button[aria-label="Play highlight"]',
        );
        if (control) {
            control.focus({ preventScroll: true });
            onboardingPlaybackFocus.current = false;
        }
    }, [coverOnboardingPlayer, onboardingGuest]);

    useEffect(() => {
        if (onboardingSession && !nextPreviewsInfo) {
            setNextPreviewsInfo(authenticated, [onboardingSession.sample], {
                initialPreviewId: onboardingSession.sample.id,
            });
        }
        if (onboardingGuest && onboardingSession.phase === 'signup') {
            navigate('/signin_new_user', { replace: true });
        } else if (onboardingGuest && onboardingSession.phase === 'complete') {
            // Older tabs can still have the former invitation saved. Resume
            // them at the phone form without another account-button step.
            openOnboardingSignup(null, onboardingSession.sampleCompleted ? 'complete' : 'continue');
        }
    }, [authenticated, navigate, nextPreviewsInfo, onboardingGuest, onboardingSession, openOnboardingSignup, setNextPreviewsInfo]);

    const clearPreviewPagerGesture = useCallback(() => {
        if (previewPagerSettleTimerRef.current !== null) {
            clearTimeout(previewPagerSettleTimerRef.current);
            previewPagerSettleTimerRef.current = null;
        }
        previewPagerGestureRef.current = null;
        previewPagerTouchGestureRef.current = null;
    }, []);

    const cancelPreviewPagerPeek = useCallback((cancelForAppearance = true) => {
        if (cancelForAppearance) {
            previewPagerPeekCancelledForAppearanceRef.current = true;
        }
        if (previewPagerPeekTimerRef.current !== null) {
            clearTimeout(previewPagerPeekTimerRef.current);
            previewPagerPeekTimerRef.current = null;
        }
        previewPagerPeekScheduledRef.current = false;
        if (previewPagerPeekAnimationRef.current) {
            previewPagerPeekAnimationRef.current.cancel();
            previewPagerPeekAnimationRef.current = null;
        }
        if (previewPagerReducedMotionCueTimerRef.current !== null) {
            clearTimeout(previewPagerReducedMotionCueTimerRef.current);
            previewPagerReducedMotionCueTimerRef.current = null;
        }
        setShowReducedMotionSwipeCue(false);
    }, []);

    const previewIndexInViewport = useCallback((previewIndex) => {
        setCurrentPreviewIndex(previewIndex);
    }, []);

    const [showAudioPlayerDrawer, setShowAudioPlayerDrawer] = useState(false);
    const [fullEpisodePreview, setFullEpisodePreview] = useState(null);
    const fullEpisodeTrack = fullEpisodePreview || track;

    const closeAudioPlayer = () => {
        audio.pause();
        setShowAudioPlayerDrawer(false);
    }

    useEffect(() => {
        if (!audio) {
            setPreviewPlayingAudioURL(null);
            return undefined;
        }

        const onPlaying = () => {
            setPreviewPlayingAudioURL(audio.src);
            if (onboardingSession && audio.src === onboardingSession.sample.mp3URL) {
                setOnboardingSampleStarted(true);
                if (!startedOnboardingIds.current.has(onboardingSession.sample.id)) {
                    startedOnboardingIds.current.add(onboardingSession.sample.id);
                    trackOnboardingEvent('onboarding_play_start');
                }
                recordOnboardingPlayback({ newId: onboardingSession.newId, previewId: onboardingSession.sample.id });
            }
        };
        const onNotPlaying = () => {
            setPreviewPlayingAudioURL(null);
            cancelPreviewPagerPeek(false);
        };

        setPreviewPlayingAudioURL(
            !audio.paused && !audio.ended && audio.readyState >= 2 ? audio.src : null,
        );
        audio.addEventListener('playing', onPlaying);
        audio.addEventListener('pause', onNotPlaying);
        audio.addEventListener('waiting', onNotPlaying);
        audio.addEventListener('stalled', onNotPlaying);
        audio.addEventListener('ended', onNotPlaying);
        audio.addEventListener('emptied', onNotPlaying);
        audio.addEventListener('loadstart', onNotPlaying);
        audio.addEventListener('error', onNotPlaying);

        return () => {
            audio.removeEventListener('playing', onPlaying);
            audio.removeEventListener('pause', onNotPlaying);
            audio.removeEventListener('waiting', onNotPlaying);
            audio.removeEventListener('stalled', onNotPlaying);
            audio.removeEventListener('ended', onNotPlaying);
            audio.removeEventListener('emptied', onNotPlaying);
            audio.removeEventListener('loadstart', onNotPlaying);
            audio.removeEventListener('error', onNotPlaying);
        };
    }, [analytics, audio, onboardingSession, cancelPreviewPagerPeek]);

    useEffect(() => {
        const onVisibilityChange = () => {
            setPageIsVisible(!document.hidden);
            if (document.hidden) cancelPreviewPagerPeek(false);
        };
        document.addEventListener('visibilitychange', onVisibilityChange);
        return () => document.removeEventListener('visibilitychange', onVisibilityChange);
    }, [cancelPreviewPagerPeek]);

    useEffect(() => {
        if (!leavingOnboarding.current && !authenticated && !nextPreviewsInfo && !onboardingSession) {
            navigate('/');
        }
    }, [authenticated, leavingOnboarding, navigate, nextPreviewsInfo, onboardingSession]);

    useEffect(() => {
        logEvent(analytics, 'screen_view', {
            firebase_screen: 'previews'
        });
    }, [analytics]);

    useEffect(() => {
        if (!authenticated || nextPreviewsInfo) memberQueueRequested.current = false;
        if (authenticated && !nextPreviewsInfo && !onboardingSession && !forMeLoadError && !memberQueueRequested.current) {
            memberQueueRequested.current = true;
            if (forMeLoadingCount === 0) {
                loadForMePreviews();
            }
        }
    }, [authenticated, forMeLoadingCount, forMeLoadError, loadForMePreviews, nextPreviewsInfo, onboardingSession])

    useEffect(() => {
        if (!authenticated || nextPreviewsInfo || onboardingSession) return undefined;
        const retry = () => retryForMeRefreshIfNeeded();
        window.addEventListener('online', retry);
        return () => window.removeEventListener('online', retry);
    }, [authenticated, nextPreviewsInfo, onboardingSession, retryForMeRefreshIfNeeded]);

    useEffect(() => {
        if (forMePreviewsData && !nextPreviewsInfo && !onboardingSession) {
            setNextPreviewsInfo(authenticated,forMePreviewsData.previews);
        }
    }, [authenticated, nextPreviewsInfo, forMePreviewsData, onboardingSession, setNextPreviewsInfo])

    const _previewsScrollContainerId = 'previewsScrollContainer';
    useLayoutEffect(() => {
        const container = previewsScrollContainerRef.current;
        if (!container || !nextPreviewsInfo || initialPositionApplied.current) return;
        // Position before viewport observers can start playback. Appending related
        // clips keeps this session mounted and must never jump back to the start.
        container.scrollTo({
            top: 0,
            left: initialPreviewIndex * (container.clientWidth || Math.min(width, 480)),
            behavior: 'instant',
        });
        initialPositionApplied.current = true;
        setPlaybackEnabled(true);
    }, [initialPreviewIndex, nextPreviewsInfo, width]);

    const previewCount = nextPreviewsInfo?.previewsInfo?.length ?? 0;
    const firstPreviewAudioURL = nextPreviewsInfo?.previewsInfo?.[0]?.mp3URL;
    const previewPagerDesktopNavigationIsVisible =
        width >= PREVIEWS_DESKTOP_NAV_MIN_WIDTH &&
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    const previewPagerSwipeIsPrimary = !previewPagerDesktopNavigationIsVisible;

    useEffect(() => {
        const previewsScrollContainer = previewsScrollContainerRef.current;
        if (!previewsScrollContainer) {
            return undefined;
        }

        const settledPageIndex = () =>
            previewPagerPageIndex(
                previewsScrollContainer.scrollLeft,
                previewsScrollContainer.clientWidth,
                previewCount - 1,
            );

        const finishGesture = () => {
            const gesture = previewPagerGestureRef.current;
            if (!gesture) {
                return;
            }
            if (gesture.kind !== 'wheel' && !gesture.released) {
                return;
            }

            const endIndex = settledPageIndex();
            if (
                !previewPagerSwipeLearnedRef.current &&
                shouldLearnPreviewPagerSwipe({
                    qualified: gesture.qualified,
                    didScroll: gesture.didScroll,
                    startIndex: gesture.startIndex,
                    settledIndex: endIndex,
                    realPreviewCount: previewCount,
                })
            ) {
                previewPagerSwipeLearnedRef.current = true;
                writePreviewPagerSwipeLearned();
                cancelPreviewPagerPeek();
            }
            clearPreviewPagerGesture();
        };

        const scheduleGestureFinish = () => {
            if (previewPagerSettleTimerRef.current !== null) {
                clearTimeout(previewPagerSettleTimerRef.current);
            }
            previewPagerSettleTimerRef.current = setTimeout(
                finishGesture,
                _PREVIEW_PAGER_SETTLE_DELAY_MS,
            );
        };

        const onPointerDown = (event) => {
            if (event.pointerType === 'touch') {
                return;
            }
            cancelPreviewPagerPeek();
            clearPreviewPagerGesture();
            previewPagerGestureRef.current = {
                kind: 'pointer',
                pointerId: event.pointerId,
                startX: event.clientX,
                startY: event.clientY,
                startIndex: settledPageIndex(),
                qualified: false,
                didScroll: false,
                released: false,
            };
        };

        const onPointerMove = (event) => {
            const gesture = previewPagerGestureRef.current;
            if (
                !gesture ||
                gesture.kind !== 'pointer' ||
                gesture.pointerId !== event.pointerId
            ) {
                return;
            }

            const horizontalDistance = Math.abs(event.clientX - gesture.startX);
            const verticalDistance = Math.abs(event.clientY - gesture.startY);
            if (
                horizontalDistance >= _PREVIEW_PAGER_POINTER_SWIPE_MIN_PX &&
                horizontalDistance > verticalDistance
            ) {
                gesture.qualified = true;
            }
        };

        const onPointerEnd = (event) => {
            const gesture = previewPagerGestureRef.current;
            if (
                !gesture ||
                gesture.kind !== 'pointer' ||
                gesture.pointerId !== event.pointerId
            ) {
                return;
            }
            if (!gesture.qualified) {
                clearPreviewPagerGesture();
                return;
            }
            gesture.released = true;
            scheduleGestureFinish();
        };

        const onPointerCancel = (event) => {
            if (event.pointerType === 'touch') {
                return;
            }
            if (previewPagerSettleTimerRef.current !== null) {
                clearTimeout(previewPagerSettleTimerRef.current);
                previewPagerSettleTimerRef.current = null;
            }
            previewPagerGestureRef.current = null;
        };

        const onTouchStart = (event) => {
            const touch = event.touches[0];
            if (!touch) {
                return;
            }
            cancelPreviewPagerPeek();
            clearPreviewPagerGesture();
            previewPagerTouchGestureRef.current = {
                kind: 'touch',
                touchIdentifier: touch.identifier,
                startX: touch.clientX,
                startY: touch.clientY,
                startIndex: settledPageIndex(),
                qualified: false,
                didScroll: false,
                released: false,
            };
        };

        const onTouchMove = (event) => {
            const gesture = previewPagerTouchGestureRef.current;
            if (!gesture || gesture.kind !== 'touch') {
                return;
            }
            const touch = Array.from(event.touches)
                .find((candidate) => candidate.identifier === gesture.touchIdentifier);
            if (!touch) {
                return;
            }

            const horizontalDistance = Math.abs(touch.clientX - gesture.startX);
            const verticalDistance = Math.abs(touch.clientY - gesture.startY);
            if (
                horizontalDistance >= _PREVIEW_PAGER_POINTER_SWIPE_MIN_PX &&
                horizontalDistance > verticalDistance
            ) {
                gesture.qualified = true;
            }
        };

        const onTouchEnd = (event) => {
            const gesture = previewPagerTouchGestureRef.current;
            if (!gesture || gesture.kind !== 'touch') {
                return;
            }
            const endedTrackedTouch = Array.from(event.changedTouches)
                .some((touch) => touch.identifier === gesture.touchIdentifier);
            if (!endedTrackedTouch) {
                return;
            }
            if (!gesture.qualified) {
                clearPreviewPagerGesture();
                return;
            }
            gesture.released = true;
            previewPagerGestureRef.current = gesture;
            previewPagerTouchGestureRef.current = null;
            scheduleGestureFinish();
        };

        const onTouchCancel = () => {
            clearPreviewPagerGesture();
        };

        const onWheel = (event) => {
            cancelPreviewPagerPeek();
            const horizontalDistance = Math.abs(event.deltaX);
            const verticalDistance = Math.abs(event.deltaY);
            if (horizontalDistance <= verticalDistance || horizontalDistance < 1) {
                return;
            }

            const currentGesture = previewPagerGestureRef.current;
            if (!currentGesture || currentGesture.kind !== 'wheel') {
                clearPreviewPagerGesture();
                previewPagerGestureRef.current = {
                    kind: 'wheel',
                    startIndex: settledPageIndex(),
                    qualified: true,
                    didScroll: false,
                };
            }
            scheduleGestureFinish();
        };

        const onScroll = () => {
            const touchGesture = previewPagerTouchGestureRef.current;
            if (touchGesture?.qualified) {
                touchGesture.didScroll = true;
            }
            const gesture = previewPagerGestureRef.current;
            if (!gesture || !gesture.qualified) {
                return;
            }
            gesture.didScroll = true;
            if (gesture.kind === 'wheel' || gesture.released) {
                scheduleGestureFinish();
            }
        };

        const onScrollEnd = () => {
            const gesture = previewPagerGestureRef.current;
            if (
                gesture?.qualified &&
                (gesture.kind === 'wheel' || gesture.released)
            ) {
                finishGesture();
            }
        };

        const onKeyDown = () => {
            cancelPreviewPagerPeek();
            clearPreviewPagerGesture();
        };

        const pointerListenerOptions = { capture: true, passive: true };
        previewsScrollContainer.addEventListener('pointerdown', onPointerDown, pointerListenerOptions);
        previewsScrollContainer.addEventListener('pointermove', onPointerMove, pointerListenerOptions);
        previewsScrollContainer.addEventListener('pointerup', onPointerEnd, pointerListenerOptions);
        previewsScrollContainer.addEventListener('pointercancel', onPointerCancel, pointerListenerOptions);
        previewsScrollContainer.addEventListener('touchstart', onTouchStart, pointerListenerOptions);
        previewsScrollContainer.addEventListener('touchmove', onTouchMove, pointerListenerOptions);
        previewsScrollContainer.addEventListener('touchend', onTouchEnd, pointerListenerOptions);
        previewsScrollContainer.addEventListener('touchcancel', onTouchCancel, pointerListenerOptions);
        previewsScrollContainer.addEventListener('wheel', onWheel, pointerListenerOptions);
        previewsScrollContainer.addEventListener('scroll', onScroll, { passive: true });
        previewsScrollContainer.addEventListener('scrollend', onScrollEnd, { passive: true });
        previewsScrollContainer.addEventListener('keydown', onKeyDown);

        return () => {
            previewsScrollContainer.removeEventListener('pointerdown', onPointerDown, true);
            previewsScrollContainer.removeEventListener('pointermove', onPointerMove, true);
            previewsScrollContainer.removeEventListener('pointerup', onPointerEnd, true);
            previewsScrollContainer.removeEventListener('pointercancel', onPointerCancel, true);
            previewsScrollContainer.removeEventListener('touchstart', onTouchStart, true);
            previewsScrollContainer.removeEventListener('touchmove', onTouchMove, true);
            previewsScrollContainer.removeEventListener('touchend', onTouchEnd, true);
            previewsScrollContainer.removeEventListener('touchcancel', onTouchCancel, true);
            previewsScrollContainer.removeEventListener('wheel', onWheel, true);
            previewsScrollContainer.removeEventListener('scroll', onScroll);
            previewsScrollContainer.removeEventListener('scrollend', onScrollEnd);
            previewsScrollContainer.removeEventListener('keydown', onKeyDown);
            clearPreviewPagerGesture();
        };
    }, [
        cancelPreviewPagerPeek,
        clearPreviewPagerGesture,
        previewCount,
    ]);

    useEffect(() => {
        const previewsScrollContainer = previewsScrollContainerRef.current;
        const previewsTrack = previewsTrackRef.current;
        const prefersReducedMotion =
            typeof window.matchMedia === 'function' &&
            window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const isAtFirstPage =
            currentPreviewIndex === 0 &&
            previewsScrollContainer &&
            Math.abs(previewsScrollContainer.scrollLeft) < 1;

        if (
            !playbackEnabled ||
            previewCount < 2 ||
            !previewPagerSwipeIsPrimary ||
            previewPlayingAudioURL !== firstPreviewAudioURL ||
            !pageIsVisible ||
            showAudioPlayerDrawer ||
            !firstPreviewAudioURL || audio?.src !== firstPreviewAudioURL ||
            !isAtFirstPage ||
            !previewsTrack ||
            (!prefersReducedMotion && typeof previewsTrack.animate !== 'function') ||
            previewPagerSwipeLearnedRef.current ||
            previewPagerPeekScheduledRef.current ||
            previewPagerPeekShownForAppearanceRef.current ||
            previewPagerPeekCancelledForAppearanceRef.current
        ) {
            return undefined;
        }

        previewPagerPeekScheduledRef.current = true;
        const peekTimer = setTimeout(() => {
            previewPagerPeekTimerRef.current = null;
            previewPagerPeekScheduledRef.current = false;

            if (
                previewPagerSwipeLearnedRef.current ||
                previewPagerPeekShownForAppearanceRef.current ||
                previewPagerPeekCancelledForAppearanceRef.current ||
                previewPlayingAudioURL !== firstPreviewAudioURL ||
                document.hidden || audio.paused || audio.ended || audio.readyState < 2 ||
                audio.src !== firstPreviewAudioURL ||
                currentPreviewIndex !== 0 ||
                Math.abs(previewsScrollContainer.scrollLeft) >= 1
            ) {
                return;
            }

            if (prefersReducedMotion) {
                previewPagerPeekShownForAppearanceRef.current = true;
                setShowReducedMotionSwipeCue(true);
                previewPagerReducedMotionCueTimerRef.current = setTimeout(() => {
                    previewPagerReducedMotionCueTimerRef.current = null;
                    setShowReducedMotionSwipeCue(false);
                }, _PREVIEW_PAGER_REDUCED_MOTION_CUE_MS);
                return;
            }

            const peekDistance = previewsScrollContainer.clientWidth / 3;
            if (peekDistance <= 0) {
                return;
            }

            previewPagerPeekShownForAppearanceRef.current = true;
            const peekTransform = `translate3d(-${peekDistance}px, 0, 0)`;
            const totalDuration =
                _PREVIEW_PAGER_PEEK_OUT_MS +
                _PREVIEW_PAGER_PEEK_HOLD_MS +
                _PREVIEW_PAGER_PEEK_BACK_MS;
            const peekAnimation = previewsTrack.animate(
                [
                    { transform: 'translate3d(0, 0, 0)', offset: 0 },
                    {
                        transform: peekTransform,
                        offset: _PREVIEW_PAGER_PEEK_OUT_MS / totalDuration,
                    },
                    {
                        transform: peekTransform,
                        offset:
                            (_PREVIEW_PAGER_PEEK_OUT_MS + _PREVIEW_PAGER_PEEK_HOLD_MS) /
                            totalDuration,
                    },
                    { transform: 'translate3d(0, 0, 0)', offset: 1 },
                ],
                {
                    duration: totalDuration,
                    easing: 'ease-in-out',
                },
            );
            previewPagerPeekAnimationRef.current = peekAnimation;
            peekAnimation.onfinish = () => {
                if (previewPagerPeekAnimationRef.current === peekAnimation) {
                    previewPagerPeekAnimationRef.current = null;
                }
            };
            peekAnimation.oncancel = peekAnimation.onfinish;
        }, _PREVIEW_PAGER_PEEK_DELAY_MS);
        previewPagerPeekTimerRef.current = peekTimer;

        return () => {
            if (previewPagerPeekTimerRef.current === peekTimer) {
                clearTimeout(peekTimer);
                previewPagerPeekTimerRef.current = null;
                previewPagerPeekScheduledRef.current = false;
            }
            if (previewPagerReducedMotionCueTimerRef.current !== null) {
                clearTimeout(previewPagerReducedMotionCueTimerRef.current);
                previewPagerReducedMotionCueTimerRef.current = null;
                setShowReducedMotionSwipeCue(false);
            }
        };
    }, [
        currentPreviewIndex,
        audio,
        firstPreviewAudioURL,
        pageIsVisible,
        playbackEnabled,
        previewCount,
        previewPlayingAudioURL,
        previewPagerSwipeIsPrimary,
        showAudioPlayerDrawer,
    ]);

    useEffect(() => () => {
        cancelPreviewPagerPeek(false);
        clearPreviewPagerGesture();
    }, [cancelPreviewPagerPeek, clearPreviewPagerGesture]);

    const scrollToPreviewIndex = (previewIndex) => {
        if (onboardingGuest && previewIndex !== currentPreviewIndex) {
            openOnboardingSignup(null, 'continue');
            return false;
        }
        cancelPreviewPagerPeek();
        clearPreviewPagerGesture();
        const container = previewsScrollContainerRef.current;
        if (!nextPreviewsInfo || !container || previewIndex < 0 || previewIndex >= previewCount) {
            return false;
        }

        if (previewIndex !== currentPreviewIndex) finishActivePreviewListening(audio, 'skipped');
        // Page only this horizontal strip. scrollIntoView also scrolls ancestor
        // viewports, which can move the framed app instead of changing clips.
        const pageWidth = container.clientWidth || (isAppFramed() ? getAppViewport().width : Math.min(width, 480));
        container.scrollTo({ top: 0, left: previewIndex * pageWidth, behavior: 'smooth' });
        return true;
    }

    const openQueue = (previewId) => {
        const previewIndex = nextPreviewsInfo?.previewsInfo?.findIndex(preview => preview.id === previewId) ?? -1;
        const preview = nextPreviewsInfo?.previewsInfo?.[previewIndex];
        const container = previewsScrollContainerRef.current;
        if (!isPlayableChatPreview(preview) || !container || onboardingGuest) return;
        cancelPreviewPagerPeek();
        clearPreviewPagerGesture();
        setCurrentPreviewIndex(previewIndex);
        setQueuePreviewId(previewId);
        // Cancel any page transition beneath the drawer and keep its opener active.
        container.scrollTo({
            top: 0,
            left: previewIndex * (container.clientWidth || Math.min(width, 480)),
            behavior: 'instant',
        });
    }

    const closeQueue = () => setQueuePreviewId(null);

    const selectQueuedPreview = (previewId) => {
        const previewIndex = nextPreviewsInfo?.previewsInfo?.findIndex(preview => preview.id === previewId) ?? -1;
        const preview = nextPreviewsInfo?.previewsInfo?.[previewIndex];
        const container = previewsScrollContainerRef.current;
        if (!isPlayableChatPreview(preview) || !container) return;
        if (onboardingGuest) {
            openOnboardingSignup(null, 'continue');
            return;
        }
        finishActivePreviewListening(audio, 'skipped');
        cancelPreviewPagerPeek();
        clearPreviewPagerGesture();
        setQueuePreviewId(null);
        finishedPreviewIds.current.delete(previewId);
        setQueuedPreviewSelection(previous => ({ previewId, serial: (previous?.serial ?? 0) + 1 }));
        setCurrentPreviewIndex(previewIndex);
        // Jump directly to the chosen card so intermediate clips cannot start.
        container.scrollTo({
            top: 0,
            left: previewIndex * (container.clientWidth || Math.min(width, 480)),
            behavior: 'instant',
        });
        // Keep playback inside the row's tap gesture for mobile browsers.
        loadTrack(preview.mp3URL, 0);
    }

    const playPreviewFinishedSound = () => {
        const sound = document.getElementById('previewfinished');
        if (!sound) return;
        try {
            sound.currentTime = 0;
            sound.playbackRate = 1;
            sound.play()?.catch(() => {
                // A blocked sound must never delay the next highlight.
            });
        } catch (_) {
            // Media preparation can fail before play() returns its promise.
        }
    };

    const nextPreview = () => {
        if (onboardingGuest) {
            openOnboardingSignup(null, 'continue');
            return;
        }
        const nextIndex = currentPreviewIndex + 1;
        if (!nextPreviewsInfo || nextIndex >= previewCount) {
            return;
        }

        scrollToPreviewIndex(nextIndex);
    }

    const prevPreview = () => {
        scrollToPreviewIndex(currentPreviewIndex - 1);
    }

    const playFullEpisode = (trackMp3URL, startSeconds, preview = track) => {

        finishActivePreviewListening(audio);

        const topicIds = [];
        for (let i = 0; i < preview.relatedTopicPreviews.length; i++) {
            topicIds.push(preview.relatedTopicPreviews[i].id);
        }

        eventLog(15, {
            'previewId': preview.id,
            'speakerIds': preview.titleSpeakerIds,
            'topicIds': topicIds,
        });
        logEvent(analytics, 'preview_card_continue_listening');
        // Keep the requested episode tied to the card that supplied its audio,
        // even if another player replaces the shared Redux track.
        setFullEpisodePreview(preview);
        loadTrack(trackMp3URL, startSeconds);
        setShowAudioPlayerDrawer(true);
    }

    const finishedPreview = (preview, index, autoAdvance) => {
        // Onboarding completion comes from the native ended event, not a
        // rounded timer or a seek to the end of the sample.
        if (onboardingGuest) return;
        const currentPreview = nextPreviewsInfo?.previewsInfo?.[currentPreviewIndex];
        if (!currentPreview || index !== currentPreviewIndex || preview.id !== currentPreview.id ||
            audio?.src !== preview.mp3URL) {
            return;
        }

        if (finishedPreviewIds.current.has(currentPreview.id)) return;
        finishedPreviewIds.current.add(currentPreview.id);

        // The cue belongs to an accepted natural auto-advance, never manual
        // navigation, the final clip, or a completion held by a drawer.
        if (autoAdvance && isPlayableChatPreview(nextPreviewsInfo.previewsInfo[index + 1]) &&
            scrollToPreviewIndex(index + 1)) {
            playPreviewFinishedSound();
        }
    }


    const closeClick = () => {
        if (onboardingGuest) {
            const position = audio?.src === onboardingSession.sample.mp3URL ? audio.currentTime : onboardingSession.position;
            audio?.pause();
            onboardingPlayAttempt.current += 1;
            onboardingPlaybackFocus.current = false;
            setOnboardingPlayPending(false);
            setOnboardingPlayError(null);
            // Retain the prepared player and exact picks so Back is reversible.
            setOnboardingSession(updateOnboardingSession({ phase: 'ready', position, reason: null, action: null }));
            return;
        }
        audio?.pause();
        if (nextPreviewsInfo?.relatedTopicId) {
            setNextPreviewsInfo(authenticated, null);
        }
        if (onboardingSession) {
            clearOnboardingSession();
            navigate('/home', { replace: true, state: { scrollToTop: true } });
            return;
        }
        navigate(-1);
    }

    return (
        <div className={`homeContainer previewsBackground${coverOnboardingPlayer ? ' onboardingPreviewCovered' : ''}`}>

            {showOnboardingReady && <OnboardingHighlightsReady
                sample={onboardingSession.sample}
                choices={onboardingChoices}
                playLabel={onboardingSession.sampleCompleted ? 'Replay highlight'
                    : onboardingSession.position > 0 ? 'Continue highlight'
                        : onboardingSession.heardPreviewIds.length > 0 || onboardingSampleStarted ? 'Play highlight' : 'Hear your first highlight'}
                onPlay={playOnboardingHighlight}
                onSelect={selectOnboardingPreview}
                onChangePicks={changeOnboardingPicks}
                error={onboardingPlayError}
                pending={onboardingPlayPending}
            />}

            <div ref={onboardingPreparedPlayerRef} className='onboardingPreparedPlayer' hidden={coverOnboardingPlayer}>
            <AudioPlayerDrawer
                open={showAudioPlayerDrawer}
                onClose={closeAudioPlayer}
                source={fullEpisodeTrack?.source ?? null}
                trackId={fullEpisodeTrack ? fullEpisodeTrack.fullTrackId ?? fullEpisodeTrack.id : null} />

            <div>
                {width >= PREVIEWS_DESKTOP_NAV_MIN_WIDTH && nextPreviewsInfo && playbackEnabled && previewCount > 0 && !onboardingGuest && !coverOnboardingPlayer && renderDesktopNavigation(
                    <nav className='previewsDesktopNav' aria-label='Preview navigation'>
                        <button
                            type='button'
                            className='desktopPrevNextButton previewsDesktopNavButtonLeft'
                            onClick={prevPreview}
                            disabled={currentPreviewIndex <= 0}
                            aria-label='Previous preview'
                            aria-controls={_previewsScrollContainerId}
                        >
                            <ChevronLeftRounded className='previewsDesktopNavIcon' />
                        </button>
                        <button
                            type='button'
                            className='desktopPrevNextButton previewsDesktopNavButtonRight'
                            onClick={nextPreview}
                            disabled={currentPreviewIndex >= previewCount - 1}
                            aria-label='Next preview'
                            aria-controls={_previewsScrollContainerId}
                        >
                            <ChevronRightRounded className='previewsDesktopNavIcon' />
                        </button>
                    </nav>
                )}

                <div className='main previewPlayerFrame'
                    onPointerDownCapture={() => cancelPreviewPagerPeek()}
                    onTouchStartCapture={() => cancelPreviewPagerPeek()}
                    onKeyDownCapture={() => cancelPreviewPagerPeek()}>
                    <div ref={setCloseButtonHost} className='previewCloseButtonHost'
                        hidden={onboardingGuest || currentPreviewIndex >= previewCount} />
                    {showReducedMotionSwipeCue && (
                        <div className='previewReducedMotionCue' aria-hidden='true'>
                            Swipe left for the next preview
                        </div>
                    )}
                    <div
                        id={_previewsScrollContainerId}
                        ref={previewsScrollContainerRef}
                        className='hslider-container hideScrollbar horizontalScroll'
                    >
                        {!nextPreviewsInfo ?
                            <div>{forMeLoadError ? <div className='preferenceFeedRefreshBanner' role='status'>
                                <span>{forMeLoadError}</span>
                                <button type='button' onClick={retryForMeRefreshIfNeeded}>Try again</button>
                            </div> : <Loading />}</div> :
                            <div ref={previewsTrackRef}>
                                {(onboardingGuest ? [onboardingSession.sample] : nextPreviewsInfo.previewsInfo).map((preview, index) => (
                                    <div key={preview.id} className='hslider-children'>
                                        <PreviewCard
                                            shouldLoadTrack={(index >= currentPreviewIndex && index <= currentPreviewIndex + _NUM_PREVIEWS_TO_PREFETCH) || preview.id === nextPreviewsInfo.relatedPreviewId}
                                            playbackEnabled={playbackEnabled && (!queuePreviewId || queuePreviewId === preview.id)}
                                            preview={preview}
                                            showsPlaybackHint={!playbackHintExpired && preview.id === playbackHintPreviewId.current}
                                            onPlaybackHintExpired={expirePlaybackHint}
                                            previewIndexInViewport={previewIndexInViewport}
                                            finishedPreview={autoAdvance => finishedPreview(preview, index, autoAdvance)}
                                            finishedPreviewIds={finishedPreviewIds}
                                            playFullEpisode={playFullEpisode}
                                            index={index}
                                            nextPreview={nextPreview}
                                            onOpenQueue={openQueue}
                                            onCloseQueue={closeQueue}
                                            onSelectQueuedPreview={selectQueuedPreview}
                                            queuedPreviewSelection={queuedPreviewSelection?.previewId === preview.id ? queuedPreviewSelection : undefined}
                                            isIntentionalPreviewExit={isIntentionalPreviewExit}
                                            closeButtonHost={onboardingGuest ? null : closeButtonHost}
                                            controlsHost={onboardingGuest ? null : controlsHost}
                                            isChromeOwner={!onboardingGuest && playbackEnabled && index === currentPreviewIndex}
                                            showAudioPlayerDrawer={showAudioPlayerDrawer}
                                            resumePreviewPosition={!queuedPreviewSelection && preview.id === nextPreviewsInfo.initialPreviewId ? resumePreviewPosition : undefined}
                                            onClose={closeClick}
                                            onboardingSession={onboardingSession?.sample.id === preview.id && nextPreviewsInfo.previewsInfo.length === 1 && !nextPreviewsInfo.relatedTopicId ? onboardingSession : null}
                                            onboardingExit={Boolean(onboardingSession)}
                                            onSignupRequired={onboardingGuest ? openOnboardingSignup : null}
                                            onSampleEnded={onboardingGuest ? completeOnboardingPreview : null}
                                        />
                                    </div>
                                ))}
                            </div>
                        }
                    </div>
                    <div ref={setControlsHost} className='previewControlsHost'
                        hidden={onboardingGuest || currentPreviewIndex >= previewCount} />
                </div>
            </div>
            </div>
        </div >
    );
}

// A new queue resets playback and pending detail requests. Appends retain the
// queue ID, preserving scroll position, finished clips, and the current audio.
export const Previews = (props) => {
    const location = useLocation();
    const currentSession = useRef(null);
    const captureSession = useCallback(snapshot => { currentSession.current = snapshot; }, []);
    useLayoutEffect(() => () => {
        const snapshot = currentSession.current;
        if (!snapshot || snapshot.location.key !== location.key) return;
        const preview = snapshot.queue.previewsInfo.find(item => item.id === snapshot.previewId);
        const audio = document.getElementById('audio');
        const audioMatchesPreview = Boolean(audio && preview?.mp3URL && audio.src === preview.mp3URL);
        // Topic taps capture the parent before replacing its audio. Preserve
        // that explicit position when the old entry unmounts with child audio.
        if (!audioMatchesPreview && getPreviewViewerSession(location)) return;
        savePreviewViewerSession(location, snapshot.queue, snapshot.previewId,
            audioMatchesPreview ? audio.currentTime : snapshot.position);
    }, [location]);
    const restoreQueue = props.restoreNextPreviewsInfo;
    const savedSession = getPreviewViewerSession(location);
    const needsQueueRestoration = Boolean(savedSession &&
        (savedSession.queue.queueId !== props.nextPreviewsInfo?.queueId ||
            savedSession.previewId !== (props.nextPreviewsInfo?.initialPreviewId || props.nextPreviewsInfo?.previewsInfo?.[0]?.id)));
    useLayoutEffect(() => {
        if (needsQueueRestoration) {
            restoreQueue(savedSession.queue, savedSession.previewId);
        }
    }, [needsQueueRestoration, restoreQueue, savedSession]);
    const onboarding = !props.authenticated ? readOnboardingSession() : null;
    const queue = props.nextPreviewsInfo?.previewsInfo;
    const onboardingQueue = onboarding && (!queue?.length ||
        (queue.length === 1 && queue[0].id === onboarding.sample.id));
    const onboardingIdentity = useRef(null);
    if (onboardingQueue && (location.state?.onboarding || location.state?.onboardingExit || props.onboardingRecovery)) {
        onboardingIdentity.current = onboarding.newId;
    }
    // Signup navigation may clear route state before the player unmounts.
    const keepOnboardingIdentity = onboardingQueue && onboardingIdentity.current === onboarding.newId;
    // Clearing the queue remounts PreviewSession before a pending route change.
    // Keep that empty session from replacing the intentional picker destination.
    const leavingOnboarding = useRef(false);
    if (needsQueueRestoration) return <Loading />;
    return <PreviewSession
        key={keepOnboardingIdentity ? `onboarding:${onboarding.newId}`
            : props.nextPreviewsInfo?.queueId ?? props.nextPreviewsInfo?.previewsInfo[0]?.id ?? 'empty'}
        {...props}
        leavingOnboarding={leavingOnboarding}
        resumePreviewPosition={savedSession?.position}
        onSessionSnapshot={captureSession}
    />;
};

const mapStateToProps = (state) => ({
    nextPreviewsInfo: state.data.nextPreviewsInfo,
    track: state.data.track,

    authenticated: state.user.authenticated,
    forMePreviewsData: state.user.forMePreviewsData,
    forMeLoadingCount: state.user.forMeLoadingCount,
    forMeLoadError: state.user.forMeLoadError,
})

const mapActionsToProps = {
    loadForMePreviews,
    retryForMeRefreshIfNeeded,
    restoreNextPreviewsInfo,
    setNextPreviewsInfo,
}

export default connect(mapStateToProps, mapActionsToProps)(Previews);
